import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { customerApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { SquareCardForm } from '@/components/customer/SquareCardForm';
import { toast } from 'sonner';
import { ShieldAlert, Lock } from 'lucide-react';

// Checkout screen for an ONLINE payment. When Square is configured on the backend, the customer
// pays with a real card via Square (card token -> backend -> Square charge). When it isn't (e.g.
// sandbox preview without an access token), a mock "simulate payment" fallback keeps the flow
// testable. The order is only ever marked PAID by the backend after payment is confirmed.
export default function MockPayment() {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(undefined);
  const [squareReady, setSquareReady] = useState(null); // null = loading, true/false once known
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    customerApi.get(`/payments/${paymentId}`).then(({ data }) => setPayment(data.payment)).catch((err) => setError(formatApiError(err)));
    customerApi.get('/payments/config/status').then(({ data }) => setSquareReady(!!data.configured)).catch(() => setSquareReady(false));
  }, [paymentId]);

  const successToast = () => toast.success(
    payment.purpose === 'TOPUP'
      ? 'Payment confirmed — your tiffins have been topped up!'
      : 'Payment confirmed — your subscription is now active!',
  );

  const payWithSquare = async (sourceId) => {
    await customerApi.post(`/payments/${paymentId}/pay-square`, { sourceId });
    successToast();
    navigate('/account');
  };

  const simulatePayment = async () => {
    setBusy(true);
    try {
      await customerApi.post(`/payments/${paymentId}/confirm-mock`);
      successToast();
      navigate('/account');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const amountLabel = payment ? `$${Number(payment.amount).toFixed(2)}` : '';

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-sm bg-white rounded-2xl border border-black/5 shadow-sm p-6 space-y-4" data-testid="mock-payment-card">
        {squareReady === true ? (
          <div className="flex items-center gap-2 text-coriander bg-coriander/10 rounded-lg px-3 py-2 text-xs" data-testid="square-live-banner">
            <Lock size={14} className="shrink-0" />
            Secure card payment by Square. Your card details never touch our servers.
          </div>
        ) : squareReady === false ? (
          <div className="flex items-center gap-2 text-amber-700 bg-amber-50 rounded-lg px-3 py-2 text-xs">
            <ShieldAlert size={14} className="shrink-0" />
            Sandbox mode — online card payments aren't configured yet, so this simulates a successful payment.
          </div>
        ) : null}

        <h1 className="font-heading text-xl font-semibold text-charcoal">
          {payment?.purpose === 'TOPUP' ? 'Complete Your Top-Up' : 'Complete Your Payment'}
        </h1>

        {error && <p className="text-sm text-destructive" data-testid="mock-payment-error">{error}</p>}
        {payment === undefined && !error && <p className="text-sm text-muted-ink">Loading…</p>}

        {payment && (
          <>
            <div className="rounded-xl bg-bone p-4 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-ink">Plan</span>
                <span className="text-charcoal">
                  {payment.purpose === 'TOPUP' ? payment.topUpPlanNameSnapshot : payment.subscription?.plan?.name}
                </span>
              </div>
              <div className="flex justify-between pt-1 border-t border-border/40">
                <span className="font-semibold text-charcoal">Amount Due</span>
                <span className="font-heading font-700 text-lg text-charcoal" data-testid="mock-payment-amount">{amountLabel}</span>
              </div>
            </div>

            {payment.status === 'PAID' ? (
              <p className="text-sm text-coriander" data-testid="mock-payment-already-paid">This payment has already been confirmed.</p>
            ) : squareReady === true ? (
              <SquareCardForm amountLabel={amountLabel} onToken={payWithSquare} />
            ) : squareReady === false ? (
              <Button
                data-testid="mock-payment-confirm-btn"
                disabled={busy}
                onClick={simulatePayment}
                className="w-full h-11 rounded-full bg-saffron hover:bg-saffron-hover transition-colors duration-150"
              >
                {busy ? 'Confirming…' : 'Simulate Successful Payment'}
              </Button>
            ) : (
              <p className="text-sm text-muted-ink">Loading payment options…</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
